import React, { createContext, useCallback, useState } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { throwExpression } from "@/utils/util";

/**
 * Context to track all queries being used by rendered components in the app. The `useTrackedQuery` hook is used to
 * register queries with this context, and unregister them when the component unmounts.
 */
interface QueryTrackingContextValue {
  /**
   * Register the provided query result.
   */
  registerQuery: (key: string, queryResult: UseQueryResult) => void;

  /**
   * Unregister the query result with the provided key. This should be called when the component using the query
   * unmounts. The query will be fully removed from the context if this is the last reference to it. If the query is
   * still being used by other components, it will remain in the context.
   *
   * In general `unregisterQuery` must be called the same number of times as `registerQuery` with the same key in order
   * for the query to be fully removed from the context.
   */
  unregisterQuery: (key: string) => void;

  /** Get the array of all tracked queries registered with the context. */
  getQueries: () => UseQueryResult[];
}

export const QueryTrackingContext = createContext<QueryTrackingContextValue | undefined>(undefined);

export const QueryTrackingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Use state to track the set of query results registered with the context
  const [queryResults, setQueryResults] = useState<Record<string, UseQueryResult>>({});

  // Keep internal count state in a ref so that it doesn't trigger any re-rendering
  // We only update React state when a query is actually added or removed (transition 0->1 or 1->0).
  const queryCountsRef = React.useRef<Record<string, number>>({});

  const registerQuery = useCallback((hashedQueryKey: string, queryResult: UseQueryResult) => {
    // Increment the count
    const newCount = (queryCountsRef.current[hashedQueryKey] = (queryCountsRef.current[hashedQueryKey] ?? 0) + 1);

    // Only add to queryResults when the first registration occurs.
    if (newCount === 1) {
      setQueryResults((prev) => ({ ...prev, [hashedQueryKey]: queryResult }));
    }
  }, []);

  const unregisterQuery = useCallback((hashedQueryKey: string) => {
    // Get the previous count
    const prevCount = queryCountsRef.current[hashedQueryKey];
    if (!prevCount) {
      throwExpression("`unregisterQuery` was called with a key that was not registered: " + hashedQueryKey);
    }

    // Decrement the count
    const newCount = (queryCountsRef.current[hashedQueryKey] = prevCount - 1);

    // Last unregister — remove from results state
    if (!newCount) {
      setQueryResults((prev) => {
        if (!(hashedQueryKey in prev)) return prev;
        const next = { ...prev };
        delete next[hashedQueryKey]; // eslint-disable-line @typescript-eslint/no-dynamic-delete
        return next;
      });
    }
  }, []);

  const getQueries = useCallback(() => Object.values(queryResults), [queryResults]);

  const contextValue = React.useMemo(
    () => ({ registerQuery, unregisterQuery, getQueries }),
    [registerQuery, unregisterQuery, getQueries],
  );

  return <QueryTrackingContext.Provider value={contextValue}>{children}</QueryTrackingContext.Provider>;
};
